/**
 * Layout helpers for group combinations in the planner (#1579 part 3).
 * Pure functions, so the row order, the block geometry and the label numbers are
 * unit-tested without rendering the grid.
 */

import type { GroupCombination, IsoWeekday, UnitCoverage } from '@/types/groupCombinations';

/** The groups of a combination or a unit: the host and its joining groups. */
export interface CombinationGroups {
  host_group_id: string;
  member_group_ids: string[];
}

const toMinutes = (hhmm: string): number => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

/**
 * Moves the groups of each combination next to each other, host first, at the
 * position of the first of its groups in the sorted list. Every other resource keeps
 * its order. A group in several combinations follows the first one.
 */
export function orderResourcesForCombinations<T extends { id: string }>(
  resources: T[],
  combinations: CombinationGroups[]
): T[] {
  if (combinations.length === 0) return resources;

  const byId = new Map(resources.map((r) => [r.id, r]));
  const combinationByGroup = new Map<string, CombinationGroups>();
  combinations.forEach((c) => {
    [c.host_group_id, ...c.member_group_ids].forEach((id) => {
      if (!combinationByGroup.has(id)) combinationByGroup.set(id, c);
    });
  });

  const placed = new Set<string>();
  const ordered: T[] = [];
  resources.forEach((resource) => {
    if (placed.has(resource.id)) return;
    const combination = combinationByGroup.get(resource.id);
    if (!combination) {
      ordered.push(resource);
      placed.add(resource.id);
      return;
    }
    [combination.host_group_id, ...combination.member_group_ids].forEach((id) => {
      const member = byId.get(id);
      if (member && !placed.has(id)) {
        ordered.push(member);
        placed.add(id);
      }
    });
  });
  return ordered;
}

/**
 * The rows a combination block spans: from its first to its last visible group.
 * Null when none of its groups is a visible row.
 */
export function combinationRowSpan(
  resourceIds: string[],
  groups: CombinationGroups
): { firstRow: number; rowCount: number } | null {
  const rows = [groups.host_group_id, ...groups.member_group_ids]
    .map((id) => resourceIds.indexOf(id))
    .filter((index) => index >= 0);
  if (rows.length === 0) return null;
  const firstRow = Math.min(...rows);
  return { firstRow, rowCount: Math.max(...rows) - firstRow + 1 };
}

/**
 * The horizontal position of a time window on the day timeline, in percent, with the
 * same math as the shift bars. Null when the window is outside the visible hours.
 */
export function timeWindowPercent(
  start: string,
  end: string,
  dayStartHour: number,
  dayEndHour: number
): { leftPercent: number; widthPercent: number } | null {
  const dayStart = dayStartHour * 60;
  const dayEnd = dayEndHour * 60;
  const from = Math.max(toMinutes(start), dayStart);
  const to = Math.min(toMinutes(end), dayEnd);
  if (to <= from) return null;
  const total = dayEnd - dayStart;
  return {
    leftPercent: ((from - dayStart) / total) * 100,
    widthPercent: ((to - from) / total) * 100,
  };
}

/** ISO weekday of a local date, 1 = Monday ... 7 = Sunday. */
export const isoWeekday = (date: Date): IsoWeekday => {
  const day = date.getDay();
  return (day === 0 ? 7 : day) as IsoWeekday;
};

const localDateKey = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** The first local date on or after from whose ISO weekday is weekday. */
export function firstDateOnWeekday(from: Date, weekday: number): Date {
  const offset = (weekday - isoWeekday(from) + 7) % 7;
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + offset);
}

/** The combinations that apply on the local date: validity and ISO weekday. */
export function activeCombinationsOn(
  combinations: GroupCombination[],
  date: Date
): GroupCombination[] {
  const key = localDateKey(date);
  const weekday = isoWeekday(date);
  return combinations.filter(
    (c) =>
      c.valid_from <= key &&
      (!c.valid_to || c.valid_to >= key) &&
      c.weekdays.includes(weekday)
  );
}

/** The role of a group in a combination, for the week view chips. */
export type CombinationRole = 'host' | 'member';

/** The combinations of one group on one date, with the group's role, by start time. */
export function combinationsForGroupOn(
  combinations: GroupCombination[],
  groupId: string,
  date: Date
): { combination: GroupCombination; role: CombinationRole }[] {
  return activeCombinationsOn(combinations, date)
    .flatMap((combination): { combination: GroupCombination; role: CombinationRole }[] => {
      if (combination.host_group_id === groupId) return [{ combination, role: 'host' }];
      if (combination.member_group_ids.includes(groupId)) return [{ combination, role: 'member' }];
      return [];
    })
    .sort((a, b) => a.combination.start_time.localeCompare(b.combination.start_time));
}

/** The numbers on a combination block label, over the whole window. */
export interface UnitSummary {
  /** Most children in any slot of the window. */
  peakChildren: number;
  /** Fewest staff scheduled in any slot. */
  minScheduled: number;
  /** Most staff required in any slot. */
  maxRequired: number;
  /** A slot has fewer staff scheduled than required. */
  shortStaffed: boolean;
}

export function summarizeUnit(unit: UnitCoverage): UnitSummary {
  if (unit.slots.length === 0) {
    return { peakChildren: 0, minScheduled: 0, maxRequired: 0, shortStaffed: false };
  }
  return {
    peakChildren: Math.max(...unit.slots.map((s) => s.children)),
    minScheduled: Math.min(...unit.slots.map((s) => s.scheduled_staff)),
    maxRequired: Math.max(...unit.slots.map((s) => s.required_staff)),
    shortStaffed: unit.slots.some((s) => s.scheduled_staff < s.required_staff),
  };
}

/** "Panda + Grizzly + Koala". */
export const combinationName = (hostName: string, memberNames: string[]): string =>
  [hostName, ...memberNames].join(' + ');
